import {
  FC,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  StyledSubMenu,
  StyledLink,
  SubmenuContainer,
  SubmenuLink,
  StyledFlex,
} from "./SubMenu.styled";
import styles from "./Header.module.scss";
import { Locale } from "src/lib/Intl";
import Link from "next/link";
import { Overlay } from "src/molecules/Overlay";
import { useLockBodyScroll } from "src/hooks/useLockBodyScroll";
import { useIsOpenSearch } from "src/hooks/stores/use-header-store";
import { headerStore } from "src/stores/header/header.store";
import { SearchBar } from "src/molecules/SearchBar";
import { NavMenuSkeleton } from "src/organisms/Header/NavMenuSkeleton";
import { getI18nRoute } from "src/utils/i18nRoutes";
import NavigationContext from "src/contexts/navigation.context";
import { useIsScrolledPast } from "src/hooks/useIsScrolledPast";
import { convertToHyphenated, sanitizeString } from "../ProductOrder/utils";
import { SearchResultDisplayedProps, useSegment } from "src/hooks/useSegment";
import { createReducer, type ReducerAction } from "src/utils/create-reducer";
import { useRouter } from "next/router";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import {
  localeFix,
  localeFixOptional,
} from "src/utils/genderLocale/genderLocale";
import { useNavigationItems } from "src/hooks/useNavigationItems";
import { NavigationItems } from "src/types/graphql.d";
import { logger } from "src/utils/helper/logger";
import type { TopLevelCategoriesData } from "src/types/graphql.d";
import NoSSR from "src/atoms/NoSSR";
import { SearchResults } from "../SearchResults";

// Max top links + top-level categories shown in the desktop submenu
const MAX_SUBMENU_ITEMS = 9;

interface Props {
  locale: Locale;
  genderId: string;
  searchAnimationTime: number;
  gendersRef?: RefObject<HTMLDivElement>;
  serviceBannerRef?: RefObject<HTMLDivElement | null>;
}

type AbandonedTrackerState = {
  hasUserInteraction: boolean;
  data: Partial<SearchResultDisplayedProps>;
};

export type AbandonedTrackerActions =
  | ReducerAction<"SET_USER_INTERACTED", boolean>
  | ReducerAction<"SET_DATA", Partial<SearchResultDisplayedProps>>;

const abandonedTrackerReducer = createReducer<
  AbandonedTrackerState,
  AbandonedTrackerActions
>({
  SET_DATA: (state, payload) => ({
    ...state,
    data: payload,
  }),
  SET_USER_INTERACTED: (state, payload) => ({
    ...state,
    hasUserInteraction: payload,
  }),
});

const abandonedTrackerInitialState: AbandonedTrackerState = {
  data: {},
  hasUserInteraction: false,
};

const SubMenu: FC<Props> = ({
  locale,
  gendersRef,
  serviceBannerRef,
  searchAnimationTime,
}) => {
  const router = useRouter();
  const { navigationItems } = useContext(NavigationContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [abandonedTrackerState, dispatchAbandonedTracker] = useReducer(
    abandonedTrackerReducer,
    abandonedTrackerInitialState
  );
  const { searchAbandoned } = useSegment();
  const { currentShopType } = useShopTypeContext();
  const { refetch, loading: clientNavigationLoading } = useNavigationItems(
    localeFixOptional({ gender: currentShopType }) || "all",
    true
  );
  const [clientNavigation, setClientNavigation] = useState<
    NavigationItems | undefined
  >();

  const showSearchOverlay = useIsScrolledPast(serviceBannerRef);

  const isOpenSearch = useIsOpenSearch();

  useLockBodyScroll(isOpenSearch);

  const searchBarContainerRef = useRef<HTMLDivElement>(null);
  const SearchResultsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputSubMenuRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const handleCloseSearch = useCallback(
    (callback?: () => void) => {
      if (inputSubMenuRef.current) {
        inputSubMenuRef.current.value = "";
      }

      if (inputRef.current) {
        inputRef.current.value = "";
      }

      if (!abandonedTrackerState.hasUserInteraction) {
        void searchAbandoned(abandonedTrackerState.data);
      }

      if (callback && typeof callback === "function") {
        callback();
      }

      setSearchTerm("");
      headerStore.closeSearch();
    },
    [
      abandonedTrackerState.data,
      abandonedTrackerState.hasUserInteraction,
      searchAbandoned,
    ]
  );

  const handleClearSearch = useCallback(() => {
    if (inputSubMenuRef.current) {
      inputSubMenuRef.current.value = "";
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }

    setSearchTerm("");
  }, []);

  // Set right before a shop-type button navigates, so the shared
  // routeChangeStart handler below skips closing the search for that one
  // navigation (the store/SubMenu survive the client-side transition).
  const keepSearchOpenRef = useRef(false);

  const handleShopTypeNavigate = useCallback(() => {
    keepSearchOpenRef.current = true;
  }, []);

  const triggerCloseSearch = useCallback(() => {
    if (keepSearchOpenRef.current) {
      keepSearchOpenRef.current = false;
      return;
    }

    if (closeButtonRef.current) {
      closeButtonRef.current.click();
    }
  }, []);

  useEffect(() => {
    router.events.on("routeChangeStart", triggerCloseSearch);
    return () => {
      router.events.off("routeChangeStart", triggerCloseSearch);
    };
  }, [triggerCloseSearch, router.events]);

  const isLinkActive = (url: string, name: string): boolean => {
    if (name === "hot deals" && router.asPath.includes("hot-deals")) {
      return true;
    }
    return router.asPath.startsWith(url);
  };

  const topLevelCategories = useMemo(
    () =>
      navigationItems?.top_level_categories?.slice(0, 4) ||
      clientNavigation?.top_level_categories?.slice(0, 4) ||
      [],
    [navigationItems?.top_level_categories, clientNavigation]
  );

  // Links and categories share one budget, so shop types with fewer
  // categories (e.g. kids) can show more links.
  const navigationLinks = useMemo(() => {
    const maxLinks = MAX_SUBMENU_ITEMS - topLevelCategories.length;
    return (
      navigationItems?.navigation_links
        ?.filter((link) => link.show_on_top)
        .slice(0, maxLinks) ||
      clientNavigation?.navigation_links
        ?.filter((link) => link.show_on_top)
        .slice(0, maxLinks) ||
      []
    );
  }, [
    navigationItems?.navigation_links,
    clientNavigation,
    topLevelCategories.length,
  ]);

  useEffect(() => {
    if (navigationItems?.navigation_links.length) {
      return;
    }

    refetch({
      shop_type: localeFixOptional({ gender: currentShopType }) || "all",
    })
      .then(({ data }) => {
        setClientNavigation(data.navigationItems || {});
      })
      .catch((error) =>
        logger({
          message: "Error fetching navigation items",
          error: error as Error,
        })
      );
  }, [navigationItems?.navigation_links, refetch, currentShopType]);

  const ref = useRef(false);

  useEffect(() => {
    if (!ref.current) {
      ref.current = true;
    }
  }, []);

  const categoryLink = (item: TopLevelCategoriesData) =>
    currentShopType
      ? `${getI18nRoute({
          route: `/${localeFix({ gender: currentShopType }) || "women"}`,
          locale,
        })}/${item.slug}`
      : `/products/${item.slug}`;

  return (
    <NoSSR>
      <StyledSubMenu isOpenSearch={isOpenSearch}>
        <nav
          className={styles.submenuContent}
          aria-labelledby="categories-select-label"
        >
          <p id="categories-select-label" className="sr-only">
            Categories
          </p>
          <SubmenuContainer
            as="ul"
            data-testid="submenu-items"
            moveLeft={isOpenSearch}
            duration={searchAnimationTime}
          >
            {(!ref.current && !navigationLinks.length) ||
            clientNavigationLoading ? (
              <NavMenuSkeleton />
            ) : (
              navigationLinks
                .map((item, index) => {
                  return (
                    <StyledLink
                      key={`submenu-${index}`}
                      hideOnDesktop={false}
                      hideOnMobile={false}
                    >
                      {item.link && item.title && (
                        <Link href={item.link} prefetch={false}>
                          <SubmenuLink
                            as="span"
                            color={item.colour}
                            active={isLinkActive(item.link, item.title)}
                            data-testid={`menu-item-${convertToHyphenated(
                              item.title
                            )}`}
                          >
                            {sanitizeString(item.title)}
                          </SubmenuLink>
                        </Link>
                      )}
                    </StyledLink>
                  );
                })
                .concat(
                  topLevelCategories.map((item, index) => {
                    const link = categoryLink(item);
                    return (
                      <StyledLink
                        key={`submenu-category-${index}`}
                        hideOnDesktop={false}
                        hideOnMobile={false}
                      >
                        {item.name && (
                          <Link href={link} passHref legacyBehavior>
                            <SubmenuLink
                              color={item.colour}
                              as="a"
                              active={isLinkActive(link, item.name)}
                              data-testid={`menu-item-${convertToHyphenated(
                                item.name
                              )}`}
                            >
                              {sanitizeString(item.name)}
                            </SubmenuLink>
                          </Link>
                        )}
                      </StyledLink>
                    );
                  })
                )
            )}
          </SubmenuContainer>
          {!showSearchOverlay ? (
            <SearchBar
              isOpenSearch={isOpenSearch}
              searchAnimationTime={searchAnimationTime}
              searchTerm={searchTerm}
              ref={inputSubMenuRef}
              setSearchTerm={setSearchTerm}
              handleCloseSearch={handleCloseSearch}
              closeButtonRef={closeButtonRef}
            />
          ) : null}
        </nav>
      </StyledSubMenu>
      {showSearchOverlay && isOpenSearch ? (
        <StyledFlex ref={searchBarContainerRef}>
          <div className={styles.searchContainer}>
            <SearchBar
              isOpenSearch={isOpenSearch}
              searchAnimationTime={searchAnimationTime}
              searchTerm={searchTerm}
              ref={inputRef}
              setSearchTerm={setSearchTerm}
              handleCloseSearch={handleCloseSearch}
              closeButtonRef={closeButtonRef}
            />
          </div>
        </StyledFlex>
      ) : null}
      <Overlay
        isOpen={isOpenSearch}
        isActive={isOpenSearch}
        onClose={triggerCloseSearch}
        excludeFromOutside={[
          gendersRef as RefObject<HTMLDivElement>,
          inputRef,
          inputSubMenuRef,
          closeButtonRef,
          searchBarContainerRef,
          SearchResultsRef,
        ]}
        mobileTop={110}
        tabletTop={173 - (showSearchOverlay ? 48 : 0)}
        delay={searchAnimationTime}
      >
        <SearchResults
          query={searchTerm}
          dispatchAbandonedTracker={dispatchAbandonedTracker}
          clearSearch={handleClearSearch}
          isSearchOpen={isOpenSearch}
          onShopTypeNavigate={handleShopTypeNavigate}
          ref={SearchResultsRef}
        />
      </Overlay>
    </NoSSR>
  );
};

export { SubMenu };
